import { useCallback, useEffect, useMemo, useState } from 'react';
import constants from '../../../../config/constants';
import { getVariant } from '../../../../utils/variant';
import { fetchImages, filterForSoftline, guessFirstImage, isVideo } from '../../ImageGalleryHelper';

/**
 * Custom hook that encapsulates all business logic for MordenImageGallery
 * Handles image loading, navigation, state management, and digital data integration
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.product - Product data object
 * @param {Object} params.appCtx - Application context
 * @param {Object} params.labels - Labels for internationalization
 * @param {Object} params.digitalData - Digital data context
 * @param {number} params.desktopImagesPerSlide - Number of images per slide on desktop
 * @param {number} params.imagesPerSlide - Number of images per slide
 * @returns {Object} - Processed data and handlers for component rendering
 */
export const useMordenImageGallery = ({
  product,
  appCtx,
  labels,
  digitalData,
  desktopImagesPerSlide,
  imagesPerSlide,
}) => {
  const { isOutOfStock, attributes, brandName, name, variants, medias, currentVariant, id } = product;
  const { sameImage } = attributes || {};

  const { regionCode, siteConfig } = appCtx || {};
  const { toggles } = siteConfig || {};
  const { isDottedImageCarouselEnabled = false, isCFMediasEnabled = false } = toggles || {};

  const variantId = !sameImage && currentVariant ? currentVariant : id;

  // Memoize expensive calculations to prevent re-renders
  const sellerName = useMemo(() => {
    const firstVariant = variants?.[0];
    const firstOffering = firstVariant?.offerings?.[0];
    return (firstOffering?.sellerName || '').toLowerCase();
  }, [variants]);

  const firstImagesGuess = useMemo(
    () => guessFirstImage(product, variantId, sameImage, 'softline'),
    [product, variantId, sameImage]
  );

  // State management
  const [selectedVariant] = useState(variantId);
  const [images, setImages] = useState(firstImagesGuess);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeIndicatorIndex, setActiveIndicatorIndex] = useState(0);
  const [carouselImages, setCarouselImages] = useState([]);
  const [isVideoPlaying, setVideoPlaying] = useState();

  // Memoize variant object to avoid duplicate getVariant calls
  const currentVariantObj = useMemo(() => getVariant(variants, currentVariant), [variants, currentVariant]);

  // Image loading effect
  useEffect(() => {
    const variantObj = currentVariantObj;

    if (!sameImage && variantObj?.medias && filterForSoftline(variantObj.medias).length > 0) {
      const variantVideosUrls = (variantObj.medias || []).filter(isVideo).map((m) => m.url);
      const productVideos = (medias || []).filter((m) => isVideo(m) && !variantVideosUrls.includes(m.url));
      setImages([...filterForSoftline(variantObj.medias), ...productVideos]);
      return;
    }

    if (sameImage && medias && filterForSoftline(medias).length > 0) {
      setImages(filterForSoftline(medias));
      return;
    }

    const media = variantObj?.medias || medias;
    fetchImages({
      variantId: selectedVariant,
      regionCode,
      sellerName,
      isCFMediasEnabled,
      media,
    })
      .then((img) => {
        if (img) {
          const productVideos = (medias || []).filter(isVideo);
          setImages([...img, ...productVideos]);
        } else {
          setImages(firstImagesGuess);
        }
      })
      .catch(() => {
        setImages(firstImagesGuess);
      });
  }, [
    currentVariant,
    currentVariantObj,
    firstImagesGuess,
    isCFMediasEnabled,
    medias,
    regionCode,
    sameImage,
    selectedVariant,
    sellerName,
    variantId,
    variants,
  ]);

  // Carousel images effect
  useEffect(() => {
    setCarouselImages(
      images.slice(activeIndicatorIndex * desktopImagesPerSlide, (1 + activeIndicatorIndex) * desktopImagesPerSlide)
    );
  }, [activeIndicatorIndex, images, desktopImagesPerSlide]);

  // Reset active image index when images change
  useEffect(() => {
    setActiveImageIndex(0);
  }, [images]);

  // Navigation handlers
  const onNextHeadline = useCallback(() => {
    let newActiveIndicatorIndex = activeIndicatorIndex;
    let newActiveImageIndex = activeImageIndex + 1;

    const isLastImage = activeImageIndex + 1 === images.length;
    const isLastImageOfSlide = (activeImageIndex + 1) % imagesPerSlide === 0;

    if (!isLastImage && isLastImageOfSlide) {
      newActiveIndicatorIndex = activeIndicatorIndex + 1;
    }

    if (isLastImage) {
      newActiveIndicatorIndex = 0;
      newActiveImageIndex = 0;
    }

    setActiveIndicatorIndex(newActiveIndicatorIndex);
    setActiveImageIndex(newActiveImageIndex);
  }, [activeImageIndex, activeIndicatorIndex, images.length, imagesPerSlide]);

  const onPreviousHeadLine = useCallback(() => {
    let newActiveIndicatorIndex = activeIndicatorIndex;
    let newActiveImageIndex = activeImageIndex - 1;

    const isFirstImage = activeImageIndex === 0;
    const isFirstImageOfSlide = activeImageIndex % imagesPerSlide === 0;

    if (isFirstImage) {
      newActiveImageIndex = images.length - 1;
      newActiveIndicatorIndex = Math.ceil(images.length / imagesPerSlide) - 1;
    } else if (!isFirstImage && isFirstImageOfSlide) {
      newActiveIndicatorIndex = activeIndicatorIndex - 1;
    }

    setActiveIndicatorIndex(newActiveIndicatorIndex);
    setActiveImageIndex(newActiveImageIndex);
  }, [activeImageIndex, activeIndicatorIndex, images.length, imagesPerSlide]);

  const handleMouseOverButton = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Utility functions
  const getImages = useCallback(() => {
    return images;
  }, [images]);

  // Digital data integration - memoize to prevent unnecessary recalculations
  const modelCharacteristics = useMemo(() => {
    const specs = attributes?.specifications || [];
    const modelo = specs.find((spec) => constants.MODEL_CHARACTERISTICS_SPECIFICATION_ID.includes(spec.name));
    return modelo && `${labels.MODEL_CHARACTERISTICS_PREFIX} ${modelo.value}`;
  }, [attributes, labels]);

  const getModelsBodyCharacteristics = useCallback(() => {
    return modelCharacteristics;
  }, [modelCharacteristics]);

  useEffect(() => {
    digitalData.pdp.setProductFeatures(modelCharacteristics ? '1' : '');
    digitalData.pdp.setProductBigImagesQty(
      // eslint-disable-next-line no-nested-ternary
      images.length > 1 ? 2 : images.length > 0 ? 1 : 0
    );
    digitalData.pdp.setProductSmallImagesQty(images.length);
  }, [digitalData, modelCharacteristics, images.length]);

  // Memoize meat stickers calculations to avoid redundant processing
  const meatStickerData = useMemo(() => {
    const isMeatStickerApplicableInPDP = !!appCtx?.siteConfig?.toggles?.isMeatStickerApplicableInPDP;
    const { meatStickers = [] } = currentVariantObj || {};
    const hasStickers = isMeatStickerApplicableInPDP && meatStickers.length > 0;
    const eventShippingMeatStickers = meatStickers.filter((sticker) => ['event'].includes(sticker.type));
    const shouldShowBadges = meatStickers.length > 0 && hasStickers;

    return {
      meatStickers,
      hasStickers,
      eventShippingMeatStickers,
      shouldShowBadges,
      isMeatStickerApplicableInPDP,
    };
  }, [appCtx?.siteConfig?.toggles?.isMeatStickerApplicableInPDP, currentVariantObj]);

  return {
    // Processed product data
    isOutOfStock,
    brandName,
    name,
    regionCode,
    isDottedImageCarouselEnabled,

    // State values
    images,
    activeImageIndex,
    activeIndicatorIndex,
    carouselImages,
    isVideoPlaying,

    // State setters
    setActiveImageIndex,
    setActiveIndicatorIndex,
    setVideoPlaying,

    // Event handlers
    onNextHeadline,
    onPreviousHeadLine,
    handleMouseOverButton,

    // Utility functions
    getImages,
    getModelsBodyCharacteristics,

    // Computed values
    modelCharacteristics,

    // Badge data
    meatStickers: meatStickerData.meatStickers,
    hasStickers: meatStickerData.hasStickers,
    eventShippingMeatStickers: meatStickerData.eventShippingMeatStickers,
    shouldShowBadges: meatStickerData.shouldShowBadges,
    isMeatStickerApplicableInPDP: meatStickerData.isMeatStickerApplicableInPDP,
  };
};
